import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Is Missionary Support Tax-Deductible in Canada? A Clear Guide for Donors (2026)';
const DESCRIPTION =
    "A plain-English guide for Canadian donors: when a gift to a missionary earns an official donation receipt for the CRA charitable donation tax credit, and when it doesn't.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover18.jpg',
            datePublished: '2026-08-14',
            dateModified: '2026-08-14',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/is-missionary-support-tax-deductible-canada',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Does a gift straight to a missionary qualify for the Canadian charitable donation tax credit?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No. Only a registered charity (or other qualified donee recognized by the CRA) can issue an official donation receipt. A personal gift to an individual missionary, however worthy the work, does not qualify — there is no receipt to claim on a tax return.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How does the charitable donation tax credit work in Canada?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "A donor gives to a CRA-registered charity, which issues an official donation receipt. The donor claims the federal charitable donation tax credit (roughly 15% on the first $200 and 29% above that, plus a provincial credit) on their T1 return, reducing tax owed rather than income.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How do Canadian missionaries offer receipt-eligible giving?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Through a mission board, denomination, or fiscal sponsor registered as a charity with the CRA. The donor gives to the charity, which must keep discretion over the funds even when it directs most of the gift toward a specific missionary's field account.",
                    },
                },
            ],
        },
    ],
};

export default function IsMissionarySupportTaxDeductibleCanada() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/is-missionary-support-tax-deductible-canada' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-tax-deductible-canada-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover18.jpg" alt="A Canadian supporter reviewing an official donation receipt" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>"Will I get a receipt for this?" is one of the first questions a Canadian supporter asks before giving to a missionary — and the answer depends entirely on the legal structure the money passes through, not on how genuine the ministry is.</p>

                    <h2>The short answer</h2>
                    <p>Only a CRA-registered charity, or another type of "qualified donee" the CRA recognizes, can issue an official donation receipt. A gift e-transferred, mailed, or handed straight to an individual missionary — even one doing full-time ministry work — does not qualify, because there's no registered charity acting as the recipient.</p>

                    <h2>Why a direct gift to a missionary doesn't get a receipt</h2>
                    <p>The CRA draws a firm line between a gift to a registered organization and a gift to a person. If a donor sends money directly to a missionary to cover living costs, that's legally a personal gift — the same as helping out a family member — and it isn't receiptable no matter how the ministry is framed. This is the case for most direct, platform-based personal support pages, including GoFundMe-style campaigns and recurring-support platforms like ours that pay a creator directly instead of routing through a registered charity.</p>

                    <h2>How receipt-eligible missionary giving actually works</h2>
                    <p>When a Canadian missionary offers a tax receipt, a CRA-registered charity sits between the donor and the missionary — typically a denominational mission board or an independent sending agency with charitable status. The donor's gift goes to the charity, which issues the official donation receipt, even though in practice most of the money is directed toward a specific missionary's field account. The charity has to retain real discretion over how the funds are used; if it simply passes a donor's gift straight through to a named individual with no discretion, the CRA can disqualify the receipt.</p>
                    <p>The donor then claims the federal charitable donation tax credit on their T1 — about 15% on the first $200 given in a year and about 29% on the amount above that — plus a separate provincial credit, which together typically recover 20–50% of the gift depending on province and income.</p>

                    <h2>Why this matters less than people assume</h2>
                    <p>The credit is real money back, but it only changes outcomes for donors who file it and give at a level where the credit is worth tracking. For a supporter giving $40/month, the paperwork and structure a charity adds is a bigger trade-off than the roughly 20% credit they'd claim. For a donor giving $5,000+ a year, the credit is substantial and often worth choosing a receipt-eligible giving structure specifically for it.</p>

                    <h2>What this means if you're choosing how to give — or built to receive</h2>
                    <p>If your Canadian supporter base is mostly small, regular monthly givers, direct support's simplicity usually outweighs a receipt most of them wouldn't file anyway. If a meaningful share of your list gives at scale and cares about the credit, routing through a registered mission board or fiscal sponsor is worth the added approval process and administrative fee.</p>
                    <p>We're upfront that Devotion Fund is a direct-support platform: gifts don't come with an official donation receipt today, because we're not a CRA-registered charity. If a receipt is the deciding factor for your support base, a Canadian mission agency or fiscal sponsor is the more honest fit.</p>

                    <h2>Frequently asked questions</h2>
                    <p><strong>Does a gift straight to a missionary qualify for the charitable donation tax credit?</strong><br />
                    No — only a CRA-registered charity or qualified donee can issue the official donation receipt the credit requires.</p>
                    <p><strong>How does the credit work?</strong><br />
                    Roughly 15% federal on the first $200 given per year and 29% above that, plus a provincial credit, claimed on the donor's T1 return.</p>
                    <p><strong>How do Canadian missionaries offer receipt-eligible giving?</strong><br />
                    Through a CRA-registered mission board or fiscal sponsor that keeps discretion over the funds rather than acting as a pure pass-through to a named individual.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/is-missionary-support-tax-deductible">Is Missionary Support Tax-Deductible? (US guide)</a></li>
                        <li><a href="/resources/is-missionary-support-tax-deductible-uk">Is Missionary Support Tax-Deductible in the UK?</a></li>
                        <li><a href="/resources/how-to-start-monthly-giving-page-without-501c3">How to Start a Monthly Giving Page Without a 501(c)(3)</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We run a direct, recurring-first support platform — which means being straight with Canadian supporters about what it doesn't do, including official donation receipts, is part of the job.</p>
                    <p><small><em>Last updated: 2026-08-14. This article is general information, not tax advice — talk to a qualified Canadian tax professional about your specific situation.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/is-missionary-support-tax-deductible-canada" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
